import {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  type MouseEventHandler,
  type ReactNode,
} from 'react';
import {
  Link as RouterLink,
  NavLink,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { FeatureFlagsContext } from 'components/shared/feature-flags';
import SideMenuDropdown from 'components/shared/side-menu/side-menu-dropdown';
import { useSideMenuContext } from 'components/shared/side-menu/side-menu-layout';
import {
  adminItems,
  getClientItems,
  getPortalItems,
} from 'components/shared/side-menu/nav-config';
import {
  BaseSideMenu,
  BaseSideMenuSection,
  BaseAccountBlock,
} from 'components/shared/side-menu/base';

import { useCurrentUser } from 'hooks/api/use-current-user';
import { useSelectedClient } from 'hooks/use-selected-client';
import { useImpersonation } from 'hooks/use-impersonation';
import { useSidebarClients } from 'components/shared/side-menu/hooks/use-sidebar-clients';

import { getCurrentYear } from 'helpers/date.helpers';
import { getInitials } from 'helpers/string.helpers';
import {
  filterVisibleItems,
  isMenuItemActive,
} from 'components/shared/side-menu/side-menu.utils';

import { ROOT_ADMIN_PAGES } from 'components/shared/side-menu/side-menu.constants';
import type { NavItem } from 'components/shared/side-menu/nav-config';

import Icons from 'assets/icons';

interface RenderLinkProps {
  to?: unknown;
  children?: ReactNode;
  className?: unknown;
  onClick?: unknown;
  end?: unknown;
  [key: string]: unknown;
}

const SideMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const user = useCurrentUser();
  const { selectedClient, selectClient, deselectClient } = useSelectedClient();
  const { impersonatingUser, isImpersonating } = useImpersonation();
  const { featureFlags } = useContext(FeatureFlagsContext);
  const { isCollapsed, toggleCollapsed } = useSideMenuContext();

  const isAdmin = !!(user?.data?.fdUser || user?.data?.clientAdmin);
  const isUserView = !!(impersonatingUser || isImpersonating());

  const sidebarClients = useSidebarClients({ skip: !isAdmin || isUserView });

  const [adminSectionExpanded, setAdminSectionExpanded] = useState(
    () => !selectedClient
  );
  const [clientSectionExpanded, setClientSectionExpanded] = useState(
    () => !!selectedClient
  );
  const currentYear = getCurrentYear();

  useEffect(() => {
    if (selectedClient) {
      setClientSectionExpanded(true);
      setAdminSectionExpanded(false);
    } else {
      setAdminSectionExpanded(true);
      setClientSectionExpanded(false);
    }
  }, [selectedClient?.id]);

  useEffect(() => {
    const clientIdFromUrl = location.pathname.match(
      /\/admin\/clients\/([^/]+)/
    )?.[1];

    if (
      clientIdFromUrl &&
      isAdmin &&
      !isUserView &&
      sidebarClients.data?.length > 0
    ) {
      if (
        !selectedClient ||
        selectedClient.id?.toString() !== clientIdFromUrl
      ) {
        const clientFromUrl = sidebarClients.data.find(
          (c) => c.id?.toString() === clientIdFromUrl
        );

        if (clientFromUrl) {
          selectClient(clientFromUrl);
        }
      }
    }
  }, [
    location.pathname,
    isAdmin,
    isUserView,
    selectedClient,
    sidebarClients.data,
    selectClient,
  ]);

  useEffect(() => {
    if (!selectedClient || !isAdmin || isUserView) {
      return;
    }

    const shouldClearClient = ROOT_ADMIN_PAGES.some(
      (path) => location.pathname === path
    );

    if (shouldClearClient) {
      deselectClient();
    }
  }, [location.pathname, selectedClient, deselectClient, isAdmin, isUserView]);

  const handleSelectClient = useCallback(
    (client: { id: string | number } | null) => {
      if (client) {
        selectClient(client);
        navigate(`/admin/clients/${client.id}/users`);
      } else {
        deselectClient();
        navigate('/admin/clients');
      }
    },
    [selectClient, deselectClient, navigate]
  );

  const toggleAdminSection = useCallback(() => {
    setAdminSectionExpanded((prev) => {
      if (!prev) {
        setClientSectionExpanded(false);
      }

      return !prev;
    });
  }, []);

  const toggleClientSection = useCallback(() => {
    setClientSectionExpanded((prev) => {
      if (!prev) {
        setAdminSectionExpanded(false);
      }

      return !prev;
    });
  }, []);

  const visibleAdminItems = filterVisibleItems(
    adminItems,
    user?.data as Record<string, unknown> | null | undefined,
    featureFlags
  );
  const clientItems = selectedClient
    ? filterVisibleItems(
        getClientItems(
          selectedClient.id,
          currentYear,
          featureFlags,
          user?.data
        ),
        user?.data as Record<string, unknown> | null | undefined,
        featureFlags
      )
    : [];
  const portalItems = getPortalItems(featureFlags);

  const isItemActive = useCallback(
    (item: NavItem) => isMenuItemActive(item, location.pathname),
    [location.pathname]
  );

  const renderLink = useCallback(
    ({ to, children, className, onClick, end, ...rest }: RenderLinkProps) => {
      const handleClick =
        typeof onClick === 'function'
          ? (onClick as MouseEventHandler<HTMLAnchorElement>)
          : undefined;

      return (
        <NavLink
          to={typeof to === 'string' ? to : ''}
          end={Boolean(end)}
          onClick={handleClick}
          className={({ isActive }) =>
            typeof className === 'function'
              ? className({ isActive })
              : className
          }
          {...(rest as Record<string, never>)}
        >
          {children}
        </NavLink>
      );
    },
    []
  );

  const getNotification = useCallback(
    (item: { notificationType?: string }) => {
      if (!item.notificationType) return false;

      if (item.notificationType === 'insights') {
        return (user?.insightsCount ?? 0) > 0;
      }

      if (item.notificationType === 'files') {
        return (user?.filesCount ?? 0) > 0;
      }

      return false;
    },
    [user?.insightsCount, user?.filesCount]
  );

  const userInfo = useMemo(() => {
    const first = (user?.data?.firstName as string) || '';
    const last = (user?.data?.lastName as string) || '';
    const fullName = `${first} ${last}`.trim() || 'User';
    const isAdminView = isAdmin && !isUserView;

    return {
      fullName,
      initials: getInitials(first, last),
      role: isAdminView
        ? 'System administration'
        : (user?.data?.client?.name as string) || 'User',
    };
  }, [
    user?.data?.firstName,
    user?.data?.lastName,
    user?.data?.client?.name,
    isAdmin,
    isUserView,
  ]);

  const profilePath = isAdmin && !isUserView ? '/admin/profile' : '/profile';

  const headerSlot = (
    <RouterLink
      to={isAdmin && !isUserView ? '/admin/clients' : '/home'}
      className="flex items-center"
      aria-label="MARKETview home"
      data-testid="side-menu-home-link"
    >
      {isCollapsed ? (
        <Icons.SmallLogo aria-hidden="true" className="size-8 shrink-0" />
      ) : (
        <Icons.LoginLogo aria-hidden="true" className="h-8 w-[130px]" />
      )}
    </RouterLink>
  );

  const topSlot = isAdmin && !isUserView && (
    <SideMenuDropdown<{ id: string | number; [key: string]: unknown }>
      items={sidebarClients.data || []}
      selectedItem={selectedClient ?? null}
      onSelectItem={handleSelectClient}
      isCollapsed={isCollapsed}
      isLoading={sidebarClients.loading}
      placeholder="Select a client..."
      searchPlaceholder="Search..."
      emptyMessage="No clients available"
      notFoundMessage="No clients found"
    />
  );

  const footerSlot = (
    <BaseAccountBlock
      user={userInfo}
      isCollapsed={isCollapsed}
      isActive={location.pathname === profilePath}
      profilePath={profilePath}
      renderLink={renderLink}
    />
  );

  return (
    <BaseSideMenu
      isCollapsed={isCollapsed}
      onToggleCollapsed={toggleCollapsed}
      headerSlot={headerSlot}
      topSlot={topSlot}
      footerSlot={footerSlot}
    >
      {isUserView && (
        <BaseSideMenuSection
          items={portalItems}
          isCollapsed={isCollapsed}
          isItemActive={isItemActive}
          renderLink={renderLink}
          getNotification={getNotification}
        />
      )}

      {isAdmin && !isUserView && (
        <>
          {selectedClient && (
            <BaseSideMenuSection
              title="Client workspace"
              items={clientItems}
              isCollapsed={isCollapsed}
              sectionIcon={<Icons.Clients className="size-5" />}
              titleIcon={<Icons.Clients className="size-5" />}
              showToggle={!isCollapsed}
              isExpanded={clientSectionExpanded}
              onToggle={toggleClientSection}
              isItemActive={isItemActive}
              renderLink={renderLink}
              getNotification={getNotification}
            />
          )}

          <BaseSideMenuSection
            title="Admin workspace"
            items={visibleAdminItems}
            isCollapsed={isCollapsed}
            sectionIcon={<Icons.Gear className="size-5" />}
            titleIcon={<Icons.Gear className="size-5" />}
            showToggle={!isCollapsed}
            isExpanded={adminSectionExpanded}
            onToggle={toggleAdminSection}
            isItemActive={isItemActive}
            renderLink={renderLink}
            getNotification={getNotification}
          />
        </>
      )}

      {!isAdmin && !isUserView && (
        <BaseSideMenuSection
          items={portalItems}
          isCollapsed={isCollapsed}
          isItemActive={isItemActive}
          renderLink={renderLink}
          getNotification={getNotification}
        />
      )}
    </BaseSideMenu>
  );
};

export default SideMenu;
